import { useLiveQuery } from "dexie-react-hooks";
import { Hash, Inbox, Layers } from "lucide-react";
import { useMemo, useState } from "react";
import { colorHex, toISODate } from "@todoer/shared";
import { Popover, usePopover } from "../components/overlay.js";
import { ProjectList } from "../components/pickers.js";
import { TaskList } from "../components/TaskList.js";
import { Chip, EmptyState, PageHeader } from "../components/ui.js";
import { db } from "../lib/db.js";

function dayLabel(day: string): string {
  const today = toISODate(new Date());
  const yesterday = toISODate(new Date(Date.now() - 86_400_000));
  if (day === today) return "Today";
  if (day === yesterday) return "Yesterday";
  return new Date(day + "T00:00").toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export function Completed() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const pop = usePopover<HTMLButtonElement>();
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const done = useLiveQuery(
    () => db.tasks.filter((t) => !!t.completedAt && (!projectId || t.projectId === projectId)).toArray(),
    [projectId], [],
  );
  const project = projects.find((p) => p.id === projectId);

  const groups = useMemo(() => {
    const sorted = [...done].sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""));
    const map = new Map<string, typeof sorted>();
    for (const t of sorted) {
      const day = toISODate(new Date(t.completedAt!));
      map.set(day, [...(map.get(day) ?? []), t]);
    }
    return [...map.entries()];
  }, [done]);

  return (
    <div>
      <PageHeader title="Completed" subtitle={`${done.length} task${done.length === 1 ? "" : "s"} done`}
        actions={<Chip onClick={pop.toggle} aria-haspopup="dialog" aria-expanded={pop.isOpen} active={!!projectId}
          icon={project ? (project.isInbox ? Inbox : Hash) : Layers} iconColor={project && !project.isInbox ? colorHex(project.color) : undefined}
          aria-label={`Project: ${project ? (project.isInbox ? "Inbox" : project.name) : "All projects"}`}>
          {project ? (project.isInbox ? "Inbox" : project.name) : "All projects"}
        </Chip>} />
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} title="Filter by project" align="end" width={260}>
          <ProjectList includeNone="All projects" value={projectId} onSelect={(id) => { setProjectId(id); pop.close(); }} />
        </Popover>
      )}
      {groups.length === 0 && <EmptyState kind="tasks" title="Nothing completed yet">Tick a task off and it shows up here.</EmptyState>}
      {groups.map(([day, tasks]) => (
        <section key={day} className="mb-6" aria-label={dayLabel(day)}>
          <h2 className="sticky top-0 z-10 -mx-1 flex items-baseline gap-2 border-b border-border bg-surface/90 px-1 py-2 text-heading font-semibold backdrop-blur-xl phone:top-[calc(44px+env(safe-area-inset-top))]">
            {dayLabel(day)}<span className="tabular text-meta font-normal text-text-faint">{tasks.length}</span>
          </h2>
          <TaskList tasks={tasks} showProject showCompletedAt hideOnComplete={false} addButton={false} />
        </section>
      ))}
      {groups.length > 0 && <p className="mt-6 text-meta text-text-faint">Repeating tasks move to their next date when completed, so they stay in your lists rather than appearing here.</p>}
    </div>
  );
}
